import { CalendarDate, fromCalendarDate, isAfter, toCalendarDate } from '../dateutils';
import {
  doesLeaseVariousOperationNeedsToBeInvoiced,
  getNumberOfMonthsFromFrequency,
  getPriceForPaymentFrequency,
} from '../leaseutils';
import { roundAtSecondDecimal } from '../numbers';
import {
  LeasePaymentFrequency,
  LeaseVariousOperation,
  LeaseVariousOperationDTO,
  LeaseVariousOperationType,
  VariousOperationType,
} from '../types';

import { getDateAndPeriodsForLeaseVariousOperationInvoice } from './invoiceHelper';

export const willBeForcedToSeparateInvoice = (
  operationFrequency: LeasePaymentFrequency,
  leaseFrequency: LeasePaymentFrequency
): boolean => {
  const leaseMonths = getNumberOfMonthsFromFrequency(leaseFrequency);
  const operationMonths = getNumberOfMonthsFromFrequency(operationFrequency);

  return operationMonths > leaseMonths;
};

export const shouldForceSeparateInvoice = (
  operation: {
    separateInvoice?: boolean;
    operationType?: LeaseVariousOperationType;
    paymentFrequency?: LeasePaymentFrequency;
  },
  leaseFrequency: LeasePaymentFrequency
): boolean => {
  if (operation.separateInvoice) {
    return true;
  }
  return willBeForcedToSeparateInvoice(operation.paymentFrequency as LeasePaymentFrequency, leaseFrequency);
};

export const getLeaseVariousOperationAmountForFrequency = (
  leaseVariousOperation: LeaseVariousOperation,
  leaseFrequency: LeasePaymentFrequency
): number => {
  if (leaseVariousOperation.type === VariousOperationType.ONE_TIME) {
    return roundAtSecondDecimal(leaseVariousOperation.amount);
  }

  const operationFrequencyMonths = getNumberOfMonthsFromFrequency(leaseVariousOperation.paymentFrequency);
  if (operationFrequencyMonths === 0) {
    return 0;
  }

  const monthlyAmount = leaseVariousOperation.amount / operationFrequencyMonths;
  return getPriceForPaymentFrequency(leaseFrequency, monthlyAmount);
};

export const getAllOccurrencesNeededForLeaseVariousOperation = (
  leaseVariousOperation: LeaseVariousOperationDTO,
  leasePeriodFrom: CalendarDate,
  leasePeriodTo: CalendarDate
): {
  invoiceDate: CalendarDate;
  periodFrom: CalendarDate;
  periodTo: CalendarDate;
}[] => {
  const operationStartDate = toCalendarDate(leaseVariousOperation.startDate);
  const checkDate = isAfter(operationStartDate, leasePeriodFrom)
    ? isAfter(operationStartDate, leasePeriodTo)
      ? leasePeriodTo
      : operationStartDate
    : leasePeriodFrom;

  const needsToBeInvoiced = doesLeaseVariousOperationNeedsToBeInvoiced(
    leaseVariousOperation as LeaseVariousOperation,
    checkDate
  );

  if (!needsToBeInvoiced) {
    return [];
  }

  const { invoiceDate, periodFrom, periodTo } = getDateAndPeriodsForLeaseVariousOperationInvoice(leaseVariousOperation);
  if (!invoiceDate || !periodFrom || !periodTo) {
    return [];
  }
  const operationEndDate = toCalendarDate(leaseVariousOperation.endDate);

  if (isAfter(invoiceDate, operationEndDate) || isAfter(periodFrom, periodTo)) {
    return [];
  }

  if (isAfter(periodFrom, leasePeriodTo)) {
    return [];
  }

  const nextOccurrences = getAllOccurrencesNeededForLeaseVariousOperation(
    { ...leaseVariousOperation, lastInvoiceDate: fromCalendarDate(periodFrom) },
    leasePeriodFrom,
    leasePeriodTo
  );

  return [{ invoiceDate, periodFrom, periodTo }, ...nextOccurrences];
};
